import { createFileRoute, Link } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { PageLayout, PageSection } from "@/components/site/PageLayout";
import { Reveal, Label } from "@/components/site/Reveal";
import { ldScript, serviceSchema } from "@/lib/schema";
import svcStructural from "@/assets/hero-structural.jpg";

export const Route = createFileRoute("/new-construction-and-custom-structural")({
  head: () => ({
    ...pageHead({
      title: "Custom Structural Engineering · SunSmart Engineering",
      description:
        "Structural engineering and sealed plan sets for new construction, screen enclosures, custom aluminum structures and retrofits, built to current code.",
      path: "/new-construction-and-custom-structural",
    }),
    scripts: [
      ldScript(
        serviceSchema({
          name: "New Construction & Custom Structural Engineering",
          description:
            "Structural engineering, sealed plan sets, and custom aluminum design for new construction, screen enclosures, and retrofits.",
          url: "/new-construction-and-custom-structural",
          serviceType: "Structural Engineering",
        }),
      ),
    ],
  }),
  component: NewConstructionPage,
});

const expertise = [
  {
    n: "01",
    title: "Custom Structural Design",
    desc: "Tailored solutions for complex and unique projects, combining creativity with engineering excellence.",
  },
  {
    n: "02",
    title: "New Construction Plans",
    desc: "Comprehensive structural designs that align with architectural plans and meet all local and state regulations.",
  },
  {
    n: "03",
    title: "Material Optimization",
    desc: "Designs that maximize efficiency and durability while minimizing costs through strategic material selection.",
  },
  {
    n: "04",
    title: "Code Compliance and Permitting",
    desc: "Expert documentation to streamline the approval process and meet building codes with confidence.",
  },
];

const whyUs = [
  {
    title: "Customized Solutions",
    desc: "Every design is crafted to suit the specific needs of your project, ensuring structural integrity and aesthetic appeal.",
  },
  {
    title: "Innovative Approach",
    desc: "We incorporate the latest design techniques and technologies to deliver results that are both efficient and cutting-edge.",
  },
  {
    title: "Seamless Collaboration",
    desc: "Our team works closely with contractors, architects, and builders to create designs that fit perfectly into your construction workflow.",
  },
  {
    title: "Safety and Longevity",
    desc: "We prioritize safety and durability, creating structures designed to withstand environmental challenges and stand the test of time.",
  },
];

function NewConstructionPage() {
  return (
    <PageLayout
      kicker="Construction Services · Structural"
      title="New Construction & Custom Structural Design"
      intro="We're experts in providing innovative and reliable structural design solutions for new construction projects. Whether you're designing a residential home, commercial property, or unique custom structure, our team ensures precision, compliance, and long-lasting results."
    >
      {/* AREAS OF EXPERTISE */}
      <PageSection label="01" title="Areas of Expertise">
        <div className="grid gap-px border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
          {expertise.map((e, i) => (
            <Reveal key={e.title} delay={i * 90}>
              <div className="bg-background p-8 h-full">
                <div className="font-display text-3xl font-black leading-none text-sun">{e.n}</div>
                <div className="mt-4 font-display text-lg font-bold uppercase leading-tight">{e.title}</div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{e.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </PageSection>

      {/* WHY CHOOSE US */}
      <PageSection label="02" title="Why Choose SunSmart" theme="surface">
        <div className="grid gap-16 lg:grid-cols-[1fr_1.1fr] lg:items-center">
          <Reveal>
            <div className="overflow-hidden rounded-sm border border-border">
              <img
                src={svcStructural}
                alt="Custom structural engineering for new construction"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>
          </Reveal>
          <div className="grid gap-4">
            {whyUs.map((w, i) => (
              <Reveal key={w.title} delay={i * 90}>
                <div className="rounded-sm border border-border bg-background p-6">
                  <div className="font-display text-lg font-bold uppercase">{w.title}</div>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </PageSection>

      {/* SUCCESS IN STRUCTURAL DESIGN */}
      <PageSection label="03" title="Success in Structural Design">
        <div className="grid gap-16 lg:grid-cols-12 lg:items-end">
          <Reveal className="lg:col-span-8">
            <p className="text-lg leading-relaxed text-muted-foreground">
              We understand the critical role that structural design plays in the success of new
              construction projects. From concept to completion, we're here to support you with
              expert engineering and designs that simplify construction while delivering
              exceptional results.
            </p>
          </Reveal>
          <Reveal delay={100}>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full bg-sun px-7 py-4 text-sm font-semibold text-ink hover:scale-[1.03] transition-transform"
              >
                Get started <span>→</span>
              </Link>
            </div>
          </Reveal>
        </div>

        {/* related services */}
        <div className="mt-16 border-t border-border pt-10">
          <Label n="→" text="Related Services" />
          <div className="mt-6 flex flex-wrap gap-4">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-6 py-3 text-sm font-semibold text-foreground hover:border-sun hover:text-sun transition-colors"
            >
              All Services
            </Link>
            <Link
              to="/pv"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-6 py-3 text-sm font-semibold text-foreground hover:border-sun hover:text-sun transition-colors"
            >
              Solar Electric (PV) Design
            </Link>
            <Link
              to="/energy-storage"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-6 py-3 text-sm font-semibold text-foreground hover:border-sun hover:text-sun transition-colors"
            >
              Energy Storage Design
            </Link>
          </div>
        </div>
      </PageSection>
    </PageLayout>
  );
}
